import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import "../styles/OutletDetail.css";
import "../styles/SelectionTooltip.css";

import ScrollToTop from "./ScrollToTop";
import ThemeToggleButton from "./ThemeToggleButton";
import { defaultOutlets } from "../data/outletInfo";
import outletNames from "../data/outletNames";
import extractOutletCode from "../utils/extractOutletCode";
import {
  loadFromCache,
  saveToCache,
  getTimestamp,
  setTimestamp,
  isFresh,
} from "../utils/cache";


import OwnershipDonutNivo from "./OwnershipDonutNivo";
import ownershipData from "../data/ownership/ownershipData.json";

const ownershipDataMap = ownershipData.reduce((acc, entry) => {
  if (entry?.outlet) acc[entry.outlet] = entry;
  return acc;
}, {});

/**
 * OutletDetail
 * - 언론사 상세정보를 보여주는 페이지
 * - 기본 하드코딩 정보 + /api/outlet 에서 가져온 최신 bias/category, url 등을 병합
 * - 향후 다른 언론사에도 재사용 가능하도록 outletName(라우트 파라미터) 사용
 */
const OutletDetail = () => {
  const { outletName } = useParams(); // e.g., '조선일보' 혹은 'chosun'
  const [dbOutlet, setDbOutlet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recentEditorials, setRecentEditorials] = useState([]);
  const [editorialsLoading, setEditorialsLoading] = useState(false);
  const [editorialsError, setEditorialsError] = useState(null);
  const [selectionTooltip, setSelectionTooltip] = useState(null);
  const outletContentRef = useRef(null);

  // URL 파라미터로 name 또는 id를 받아 매칭
  const normalizedKey = useMemo(() => {
    if (!outletName) return null;
    const lower = decodeURIComponent(outletName).toLowerCase();
    if (lower.includes('hani') || lower.includes('한겨레')) return '한겨레';
    if (lower.includes('khan') || lower.includes('경향신문')) return '경향신문';
    if (lower.includes('nocutnews') || lower.includes('노컷뉴스')) return '노컷뉴스';
    if (lower.includes('pressian') || lower.includes('프레시안')) return '프레시안';
    if (lower.includes('mediatoday') || lower.includes('미디어오늘')) return '미디어오늘';
    if (lower.includes('ohmynews') || lower.includes('오마이뉴스')) return '오마이뉴스';
    if (lower.includes('sisain') || lower.includes('시사인')) return '시사인';
    if (lower.includes('imbc') || lower.includes('mbc')) return 'MBC';
    if (lower.includes('jtbc')) return 'JTBC';

    if (lower.includes('chosun') || lower.includes('조선')) return '조선일보';
    if (lower.includes('joongang') || lower.includes('중앙일보')) return '중앙일보';
    if (lower.includes('donga') || lower.includes('동아일보')) return '동아일보';
    // if (lower.includes('yna') || lower.includes('연합뉴스')) return '연합뉴스';
    // if (lower.includes('ytn') || lower.includes('YTN')) return 'YTN';
    if (lower.includes('kmib') || lower.includes('국민일보')) return '국민일보';
    if (lower.includes('munhwa') || lower.includes('문화일보')) return '문화일보';
    if (lower.includes('segye') || lower.includes('세계일보')) return '세계일보';
    if (lower.includes('seoul') || lower.includes('서울신문')) return '서울신문'; // 중도 ?
    if (lower.includes('ichannela') || lower.includes('채널a')) return '채널A';
    // if (lower.includes('kbs')) return 'KBS';
    // if (lower.includes('sbs')) return 'SBS';
    if (lower.includes('mbn')) return 'MBN';
    
    if (lower.includes('newsis') || lower.includes('뉴시스')) return '뉴시스';
    if (lower.includes('naeil') || lower.includes('내일신문')) return '내일신문';
    if (lower.includes('hankookilbo') || lower.includes('한국일보')) return '한국일보';
    return null;
  }, [outletName]);

  const outletInfo = normalizedKey ? defaultOutlets[normalizedKey] : null;

  // DB의 outlet 컬렉션과 병합 (bias.category, url, type 등)
  useEffect(() => {
    let alive = true;

    setLoading(true);
    setDbOutlet(null);

    const findOutlet = (list) => {
      if (!Array.isArray(list)) return null;
      return (
        list.find(
          (d) => d && (d.name === outletInfo?.name || d.id === outletInfo?.id)
        ) ||
        null
      );
    };

    const normalizeOutlet = (outlet) => {
      if (!outlet) return null;
      return {
        ...outlet,
        biasCategory:
          outlet.biasCategory ||
          outlet?.bias?.category ||
          defaultOutlets[outlet.name]?.bias?.category ||
          null,
      };
    };

    const loadOutlet = async () => {
      try {
        const cached = loadFromCache("cachedOutlets");
        const cachedTimestamp = getTimestamp("outletsTimestamp");
        const cachedList = cached
          ? Array.isArray(cached)
            ? cached
            : Object.values(cached)
          : null;

        if (cachedList && isFresh(cachedTimestamp)) {
          const found = findOutlet(cachedList.map(normalizeOutlet));
          if (alive) {
            setDbOutlet(found);
            setLoading(false);
          }
          if (found) {
            return;
          }
        }

        const res = await fetch("/api/outlets/compact");
        if (!res.ok) {
          throw new Error("언론사 정보를 불러오지 못했습니다.");
        }
        const data = await res.json();
        const fetchedArray = Array.isArray(data) ? data : [];
        const outletsMap = {};
        fetchedArray.forEach((doc) => {
          if (doc?.id) {
            outletsMap[doc.id] = {
              ...doc,
              biasCategory:
                doc.biasCategory ||
                defaultOutlets[doc.name]?.bias?.category ||
                null,
            };
          }
        });
        saveToCache("cachedOutlets", outletsMap);
        setTimestamp("outletsTimestamp");

        const normalizedList = Object.values(outletsMap);
        const found = findOutlet(normalizedList);
        if (alive) {
          setDbOutlet(found);
          setLoading(false);
        }
      } catch (e) {
        if (alive) {
          setDbOutlet(null);
          setLoading(false);
        }
      }
    };

    if (outletInfo) {
      loadOutlet();
    } else {
      setLoading(false);
    }

    return () => {
      alive = false;
    };
  }, [normalizedKey, outletInfo]);

  const merged = useMemo(() => {
    const fromDb = dbOutlet
      ? {
          id: dbOutlet.id,
          name: dbOutlet.name,
          type: dbOutlet.type,
          url: dbOutlet.url,
          biasCategory:
            dbOutlet.biasCategory || dbOutlet?.bias?.category || null,
          updatedAt: dbOutlet.updatedAt,
        }
      : {};
    if (!outletInfo) return fromDb;
    return {
      ...outletInfo,
      ...fromDb,
    };
  }, [dbOutlet, outletInfo]);

  const isBroadcastOutlet = useMemo(() => {
    const typeValue =
      (merged?.type || outletInfo?.type || "").toString().toLowerCase();
    if (typeValue.includes("방송")) return true;
    const publishFormat =
      merged?.profile?.발행_형태 || outletInfo?.profile?.발행_형태 || "";
    if (typeof publishFormat !== "string") return false;
    return /방송사|지상파|라디오|종합편성|케이블/.test(publishFormat);
  }, [merged?.type, merged?.profile?.발행_형태, outletInfo?.type, outletInfo?.profile?.발행_형태]);

  const biasClass = useMemo(() => {
    const cat = merged.biasCategory || '보수';
    if (cat.includes('진보')) return 'outlet-bias-tag progressive';
    if (cat.includes('중도')) return 'outlet-bias-tag moderate';
    if (cat.includes('보수')) return 'outlet-bias-tag conservative';
    return 'outlet-bias-tag unknown';
  }, [merged.biasCategory]);

  const biasSummaryItems = useMemo(() => {
    const summary = merged.profile?.보도_성향_요약;
    if (!summary) return [];
    return Array.isArray(summary) ? summary : [summary];
  }, [merged.profile?.보도_성향_요약]);

  const outletDisplayName = merged.name || outletInfo?.name || normalizedKey;

  const ownershipChartInfo = useMemo(() => {
    const key = merged.name || outletInfo?.name;
    if (!key) return null;
    return ownershipDataMap[key] || null;
  }, [merged.name, outletInfo.name]);

  const hasOwnershipVisual =
    !!merged.소유구조_지배구조?.사진 || (ownershipChartInfo?.data?.length ?? 0) > 0;

  const hideSelectionTooltip = useCallback(() => {
    setSelectionTooltip(null);
  }, []);

  useEffect(() => {
    const handleMouseUp = () => {
      window.setTimeout(() => {
        const selection = window.getSelection();
        if (!selection || selection.isCollapsed) {
          hideSelectionTooltip();
          return;
        }

        const rawText = selection.toString().trim();
        if (!rawText) {
          hideSelectionTooltip();
          return;
        }

        const normalizedText = rawText.replace(/\s+/g, " ");
        if (normalizedText.length > 48) {
          hideSelectionTooltip();
          return;
        }
        const words = normalizedText.split(" ");
        if (words.length > 8) {
          hideSelectionTooltip();
          return;
        }

        if (selection.rangeCount === 0) {
          hideSelectionTooltip();
          return;
        }

        const range = selection.getRangeAt(0);
        const containerNode =
          range.commonAncestorContainer?.nodeType === Node.TEXT_NODE
            ? range.commonAncestorContainer.parentNode
            : range.commonAncestorContainer;

        if (
          !outletContentRef.current ||
          !containerNode ||
          !outletContentRef.current.contains(containerNode)
        ) {
          hideSelectionTooltip();
          return;
        }

        const rects = range.getClientRects();
        const rect =
          rects.length > 0
            ? rects[rects.length - 1]
            : range.getBoundingClientRect();

        if (!rect || (rect.width === 0 && rect.height === 0)) {
          hideSelectionTooltip();
          return;
        }

        const windowWidth =
          window.innerWidth || document.documentElement.clientWidth;

        const tooltipWidth = 280;
        const tooltipHeight = 44;

        const preferredTop = rect.top - tooltipHeight - 12;
        const top = preferredTop > 0 ? preferredTop : rect.bottom + 12;

        let left = rect.left + rect.width / 2;
        left = Math.max(tooltipWidth / 2 + 16, left);
        left = Math.min(windowWidth - tooltipWidth / 2 - 16, left);

        setSelectionTooltip({
          text: normalizedText,
          top,
          left,
        });
      }, 0);
    };

    const handleKeyUp = (event) => {
      if (event.key === "Escape") {
        hideSelectionTooltip();
      }
    };

    const handleScroll = () => {
      hideSelectionTooltip();
    };

    document.addEventListener("mouseup", handleMouseUp);
    document.addEventListener("keyup", handleKeyUp);
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      document.removeEventListener("mouseup", handleMouseUp);
      document.removeEventListener("keyup", handleKeyUp);
      window.removeEventListener("scroll", handleScroll);
    };
  }, [hideSelectionTooltip]);

  useEffect(() => {
    if (isBroadcastOutlet) {
      setRecentEditorials([]);
      setEditorialsLoading(false);
      setEditorialsError(null);
      return;
    }

    if (!outletDisplayName || !outletInfo) return;

    const normalize = (value) =>
      (value || '')
        .toString()
        .replace(/\s+/g, '')
        .replace(/·/g, '')
        .toLowerCase();

    const targetKey = normalize(outletDisplayName);
    if (!targetKey) return;

    const cacheKey = `outletEditorials:${targetKey}`;
    const cacheTimestampKey = `${cacheKey}:timestamp`;

    const cached = loadFromCache(cacheKey);
    const cachedTimestamp = getTimestamp(cacheTimestampKey);
    if (cached && Array.isArray(cached) && isFresh(cachedTimestamp)) {
      setRecentEditorials(cached);
      setEditorialsLoading(false);
      return;
    }

    let isMounted = true;
    setEditorialsLoading(true);
    setEditorialsError(null);

    (async () => {
      try {
        const response = await fetch("/api/editorials");
        if (!response.ok) {
          throw new Error(`사설 데이터를 불러오지 못했습니다. (${response.status})`);
        }
        const data = await response.json();
        if (!Array.isArray(data)) {
          throw new Error('올바르지 않은 사설 데이터 형식입니다.');
        }

        const candidateKeys = [
          targetKey,
          normalize(outletInfo?.id),
          normalize(normalizedKey),
        ].filter(Boolean);

        const resolveOutletKey = (item) => {
          const direct = normalize(item.outlet);
          if (direct) return direct;
          const url = item.source_url || item.sourceUrl || item.link || item.url;
          if (!url) return "";
          const extracted = extractOutletCode(url, outletNames);
          return normalize(extracted);
        };

        const filtered = data
          .filter((item) => {
            const source = resolveOutletKey(item);
            if (!source || source === "unknown") return false;
            return candidateKeys.some(
              (key) => source === key || source.includes(key) || key.includes(source)
            );
          })
          .sort((a, b) => {
            const left = new Date(b.date || b.publishedAt || b.published_at || 0).getTime();
            const right = new Date(a.date || a.publishedAt || a.published_at || 0).getTime();
            return left - right;
          })
          .slice(0, 10)
          .map((item) => ({
            id: item._id || item.id || item.link || item.source_url,
            title: item.title || '제목 없음',
            url: item.source_url || item.link || '#',
            date: item.date || item.publishedAt || item.published_at || null,
          }));

        if (isMounted) {
          setRecentEditorials(filtered);
          saveToCache(cacheKey, filtered);
          setTimestamp(cacheTimestampKey);
        }
      } catch (error) {
        if (isMounted) {
          setEditorialsError(
            error instanceof Error ? error.message : '사설 데이터를 가져오는 중 문제가 발생했습니다.'
          );
        }
      } finally {
        if (isMounted) {
          setEditorialsLoading(false);
        }
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [outletDisplayName, outletInfo?.id, normalizedKey, isBroadcastOutlet]);

  const formatEditorialDate = (raw) => {
    if (!raw) return null;
    try {
      return new Date(raw).toLocaleDateString('ko-KR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      });
    } catch (e) {
      return null;
    }
  };

  return (
    <div className="outlet-detail-page" ref={outletContentRef}>
      <div className="outlet-detail-hero">
        <div className="outlet-detail-hero__title">
          <div className="outlet-title-container">
            <h1>{merged.name || outletInfo.name}</h1>
            {(merged.url || merged.sns_links?.length) && (
              <div className="sns-links-container">
                {merged.url && (
                  <a
                    className="sns-link sns-link--home"
                    href={merged.url}
                    target="_blank"
                    rel="noreferrer"
                    aria-label="공식 사이트"
                    title="공식 사이트"
                  >
                    <img src="/home.svg" alt="" className="sns-logo" aria-hidden="true" />
                  </a>
                )}
                {merged.sns_links?.map((link) => (
                  <a
                    href={link.url}
                    key={link.platform}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="sns-link"
                  >
                    <img
                      src={`/${link.platform}_logo.svg`}
                      alt={`${link.platform} logo`}
                      className="sns-logo"
                    />
                  </a>
                ))}
              </div>
            )}
          </div>
          <div className="outlet-meta">
            {merged.type && <span className="meta-chip">{merged.type}</span>}
            <span className={biasClass}>{merged.biasCategory || '보수'}</span>
            {/* {merged.updatedAt && <span className="meta-chip">마지막 업데이트: {merged.updatedAt}</span>} */}
          </div>

        </div>
      </div>

      <div className="outlet-detail-container">
        <div className="outlet-sections">
          <section className="outlet-info-section">
            <div className="outlet-info-grid">
              <div className="outlet-info-block">
                <h3>주요 정보</h3>
                <p className="lead">{merged.summary}</p>
                <div className="kv-grid">
                  <div><span className="kv-k">설립</span><span className="kv-v">{merged.profile?.설립}</span></div>
                  <div><span className="kv-k">창간</span><span className="kv-v">{merged.profile?.창간}</span></div>
                  <div><span className="kv-k">본사 위치</span><span className="kv-v">{merged.profile?.본사_위치}</span></div>
                  <div><span className="kv-k">발행 형태</span><span className="kv-v">{merged.profile?.발행_형태}</span></div>
                  <div><span className="kv-k">회장/사장</span><span className="kv-v">{merged.profile?.경영진.회장} / {merged.profile?.경영진.사장}</span></div>
                  {/* <div>
                    <span className="kv-k">보도 성향</span>
                    <span className="kv-v">
                      {biasSummaryItems.length > 0 ? (
                        <ul className="bullet-list">
                          {biasSummaryItems.map((item, idx) => (
                            <li key={`${item}-${idx}`}>{item}</li>
                          ))}
                        </ul>
                      ) : null}
                    </span>
                  </div> */}
                </div>
              </div>
              <div className="outlet-info-block">
                <h3>소유구조 · 지배구조</h3>
                {merged.소유구조_지배구조?.요약 && <p className="lead">{merged.소유구조_지배구조.요약}</p>}
                {hasOwnershipVisual ? (
                  <div className="ownership-visual">
                    {ownershipChartInfo?.data?.length > 0 && (
                      <div className="ownership-chart-wrap">
                        <OwnershipDonutNivo
                          data={ownershipChartInfo.data}
                        />
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="ownership-empty-wrap">
                    <p className="note ownership-empty">공개된 지분구조 없음</p>
                  </div>
                )}
              </div>
            </div>
          </section>

          <section className="outlet-editorials">
            <div className="outlet-editorials__header">
              <h2>최근 사설</h2>
              {!isBroadcastOutlet && editorialsLoading && (
                <span className="outlet-editorials__status">불러오는 중...</span>
              )}
            </div>

            {isBroadcastOutlet ? (
              <p className="outlet-editorials__message">
                방송사는 별도의 사설 지면이 없어 사설이 제공되지 않습니다.
              </p>
            ) : editorialsError ? (
              <p className="outlet-editorials__message">{editorialsError}</p>
            ) : recentEditorials.length === 0 ? (
              <p className="outlet-editorials__message">해당 언론사는 사설 데이터 제공을 허용하지 않습니다.</p>
            ) : (
              <ul className="outlet-editorials__list">
                {recentEditorials.map((item) => (
                  <li key={item.id} className="outlet-editorials__item">
                    <a
                      href={item.url || '#'}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="outlet-editorials__link"
                    >
                      <span className="outlet-editorials__title">{item.title}</span>
                      {formatEditorialDate(item.date) && (
                        <time className="outlet-editorials__date" dateTime={item.date}>
                          {formatEditorialDate(item.date)}
                        </time>
                      )}
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </section>


        </div>

        <div className="outlet-detail-footer">
          <Link to="/outlet" className="back-link">← 이전으로</Link>
          {loading && <span className="loading">DB에서 최신 정보를 불러오는 중…</span>}
        </div>
      </div>
      <ThemeToggleButton />
      <ScrollToTop/>
      {selectionTooltip && (
        <div
          className="selection-tooltip"
          style={{
            top: `${selectionTooltip.top}px`,
            left: `${selectionTooltip.left}px`,
          }}
        >
          <span className="selection-tooltip__label">
            {selectionTooltip.text.length > 18
              ? `${selectionTooltip.text.slice(0, 18)}…`
              : selectionTooltip.text}
          </span>
          <a
            href={`https://namu.wiki/w/${encodeURIComponent(
              selectionTooltip.text
            )}`}
            target="_blank"
            rel="noopener noreferrer"
            className="selection-tooltip__link"
            onClick={() => {
              window.setTimeout(() => {
                hideSelectionTooltip();
              }, 0);
            }}
          >
            나무위키에서 보기
          </a>
        </div>
      )}
    </div>
  );
};

export default OutletDetail;
